/**
 * A titled part of a screen: a heading in the page outline, a count or actions beside it, and the
 * body. The heading names the region, so a screen reader can jump between sections.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useId } from "react";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { Heading, Text } from "./Text.tsx";
export interface SectionProps {
  readonly title: string;
  /** The title's place in the page outline. The page title is level 1. */
  readonly level?: 2 | 3;
  /** A number or short phrase after the title: "12", "3 unread". */
  readonly count?: string;
  readonly actions?: ReactNode;
  /** Moves focus to the title when the section appears, for a screen the person arrives at from elsewhere. */
  readonly focusTitle?: boolean;
  readonly testId?: string;
  readonly children: ReactNode;
}
export function Section({
  title,
  level = 2,
  count,
  actions,
  focusTitle = false,
  testId,
  children
}: SectionProps) {
  const titleId = useId();
  return <section aria-labelledby={titleId} data-testid={testId} className="x78zum5 xdt5ytf x626il7 xeuugli">
      <div className="x78zum5 x1a02dak x6s0dn4 x1qughib x626il7">
        <div className="x78zum5 x1a02dak x1pha0wt x1173whf">
          <Heading level={level} id={titleId} focusOnMount={focusTitle}>
            {title}
          </Heading>
          {count === undefined ? null : <Text as="span" variant="caption" tone="secondary" numeric>
              {count}
            </Text>}
        </div>
        {actions === undefined ? null : <div className="x78zum5 x1a02dak x1173whf">{actions}</div>}
      </div>
      {children}
    </section>;
}